<script setup>
import {computed, onMounted, ref} from "vue";
import {useStore} from "vuex";
import {useInfiniteScroll} from "@vueuse/core";
import {throttle} from "lodash/function";
import UntrackedKeywordTableHeader from "@/components/keyword/UntrackedKeywordTableHeader.vue";
import UntrackedKeywordCompetitorRanking from "@/components/keyword/UntrackedKeywordCompetitorRanking.vue";
import UntrackedKeywordsSearchAndExport from "@/components/keyword/UntrackedKeywordsSearchAndExport.vue";
import UntrackedKeywordDatesFilter from "@/components/keyword/UntrackedKeywordDatesFilter.vue";
import ChangeOfVolumeTag from "@/components/keyword/partials/ChangeOfVolumeTag.vue";
import DifficultyTag from "@/components/global/assets/DifficultyTag.vue";

const store = useStore();

const loading = computed(() => store.getters['untrackedKeywords/loading']);
const silentLoading = computed(() => store.getters['untrackedKeywords/silentLoading']);
const keywords = computed(() => store.getters['untrackedKeywords/untrackedKeywords']);
const competitorRanking = computed(() => store.getters['untrackedKeywords/competitorRanking']);

onMounted(async () => await store.dispatch('untrackedKeywords/fetchKeywords'));

const infiniteScroll = ref(null);

useInfiniteScroll(
  infiniteScroll,
  async () => {
    if (store.getters['untrackedKeywords/hasMorePages'] && silentLoading.value === false) {
      fetchKeywords();
    }
  },
  {distance: 10}
)

const noKeywordsMessage = computed(() => {
  const totalKeywords = competitorRanking.value?.total_keywords;
  const keywordLength = keywords.value.length;

  if (keywordLength < 1 && totalKeywords === 1) {
    return `You are currently tracking ${totalKeywords} available keyword.`;
  }

  if (keywordLength < 1 && totalKeywords > 0) {
    return `You are currently tracking all ${Number(totalKeywords).toLocaleString()} keywords.`;
  }

  return 'Looks like there are no keywords here.';
});

const fetchKeywords = throttle(async () => {
  await store.dispatch('untrackedKeywords/fetchKeywords', {fetchFreshData: false, silentLoading: true});
}, 500);
</script>

<template>
  <div class="tw-flex tw-flex-col tw-mb-12">
    <h1 class="tw-text-4.5xl tw-text-font-primary tw-font-bold tw-font-sans">
      Keyword Scan
    </h1>
    <p class="tw-text-font-secondary tw-text-base">
      Discover hidden keyword rankings with Keyword Scan. Tracking every 90 days, we reveal the keywords you're ranking for but haven't selected in your SEO strategy. <br>Check-in monthly to track progress and uncover new opportunities.
    </p>
  </div>

  <UntrackedKeywordDatesFilter />

  <div class="tw-bg-white tw-rounded-lg tw-p-8 tw-flex tw-flex-col tw-space-y-8 tw-flex-1 tw-overflow-hidden">
    <UntrackedKeywordCompetitorRanking />
    <UntrackedKeywordsSearchAndExport />
    <UntrackedKeywordTableHeader />

    <template v-if="loading">
      <div
        class="tw-flex tw-flex-col tw-items-center tw-space-y-10 tw-justify-center tw-my-24 tw-w-full tw-max-w-sm tw-mx-auto"
      >
        <svg-vue
          icon="icons/loading"
          class="tw-w-1/2 tw-h-auto tw-text-blue/20"
        />
        <p class="tw-text-2xl tw-text-center">
          Loading
        </p>
      </div>
    </template>


    <div
      v-else-if="keywords.length > 0"
      ref="infiniteScroll"
      class="tw-overflow-y-auto tw-h-[75vh]"
    >
      <div
        class="tw-rounded-xl tw-border tw-border-grey-light tw-text-grey-dark"
      >
        <div
          v-for="(keyword, index) in keywords"
          :key="index"
          class="tw-grid tw-grid-cols-12 tw-h-24 tw-overflow-hidden tw-border-grey-light"
          :class="{
            'tw-border-b': index !== keywords.length - 1,
            'tw-bg-grey-highlight tw-text-grey': keyword.status === 'LOST',
            'tw-rounded-b-xl': index === keywords.length - 1 && keyword.status === 'LOST',
            'tw-rounded-t-xl': index === 0 && keyword.status === 'LOST',
          }"
        >
          <div class="tw-col-span-3 tw-px-4 tw-my-auto">
            {{ keyword.keyword }}
          </div>

          <div class="tw-px-4 tw-col-span-1 tw-my-auto tw-mx-auto tw-font-bold">
            <div class="tw-flex tw-items-center tw-justify-center tw-gap-3">
              <svg-vue
                v-if="keyword.status === 'LOST'"
                v-tooltip="{
                  content: 'Lost keyword',
                  placement: 'bottom',
                  theme: 'dark-blue-tooltip',
                }"
                icon="icons/lost"
                class="tw-w-6 tw-h-6 tw-cursor-pointer"
              />
              <svg-vue
                v-if="keyword.status === 'NEW'"
                v-tooltip="{
                  content: 'New keyword',
                  placement: 'bottom',
                  theme: 'dark-blue-tooltip',
                }"
                icon="icons/new"
                class="tw-w-6 tw-h-6 tw-cursor-pointer"
              />
              <span>{{ keyword.position }}</span>
              <ChangeOfVolumeTag
                v-if="keyword.position_change !== null && keyword.position_change !== undefined"
                :number="keyword.position_change"
              />
            </div>
          </div>

          <div class="tw-px-4 tw-col-span-1 tw-my-auto tw-mx-auto">
            {{ keyword.search_volume > 0 ? keyword.search_volume.toLocaleString() : '< 10' }}
          </div>

          <div class="tw-px-4 tw-col-span-1 tw-my-auto tw-mx-auto">
            <DifficultyTag
              :difficulty=" keyword.difficulty"
            />
          </div>

          <div
            class="tw-col-span-3 tw-flex tw-flex-col tw-items-start tw-justify-center tw-my-auto tw-overflow-scroll tw-px-4"
          >
            <a
              :href="keyword.url"
              class="tw-underline"
              :class="[
                keyword.status === 'LOST' ? 'tw-text-grey' : 'tw-text-blue'
              ]"
            >
              {{ keyword.relative_url }}
            </a>
            <span
              v-if="keyword.old_relative_url && keyword.old_relative_url !== keyword.relative_url"
              class="tw-text-grey tw-flex tw-items-center tw-justify-center"
            >
              <svg-vue
                icon="icons/arrows"
                class="tw-w-6 tw-h-6"
              />
              <span class="tw-line-through">
                {{ keyword.old_relative_url }}
              </span>
            </span>
          </div>

          <div class="tw-px-4 tw-col-span-2 tw-my-auto tw-mx-auto">
            {{ keyword.last_checked }}
          </div>

          <div class="tw-px-4 tw-col-span-1 tw-my-auto tw-mx-auto">
            <button
              v-tooltip="{
                content: 'Add to your tracked keywords',
                placement: 'bottom',
                theme: 'dark-blue-tooltip',
              }"
              class="tw-border-2 tw-border-blue tw-rounded-lg tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center tw-transition-all tw-cursor-pointer hover:tw-shadow-blue tw-text-blue tw-bg-white"
              @click="store.dispatch('untrackedKeywords/addKeyword', keyword)"
            >
              <svg-vue
                icon="icons/keyword-add"
                class="tw-w-6 tw-h-6 tw-flex-shrink-0"
              />
            </button>
          </div>
        </div>
        <div
          v-if="silentLoading"
          class="tw-grid tw-h-20 tw-overflow-hidden tw-border-t tw-border-grey-light"
        >
          <div class="tw-flex tw-items-center tw-gap-2 tw-m-auto tw-px-4">
            <svg-vue
              icon="icons/loading"
              class="tw-w-6 tw-text-blue/20"
            />
            Loading more keywords...
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="tw-flex tw-flex-col tw-items-center tw-space-y-10 tw-justify-center tw-my-24 tw-w-full tw-max-w-sm tw-mx-auto tw-h-[30vh]"
    >
      <svg-vue
        icon="illustrations/landscape-blue"
        class="tw-w-56"
      />

      <p class="tw-text-2xl tw-text-center tw-whitespace-nowrap">
        {{ noKeywordsMessage }}
      </p>
    </div>
  </div>
</template>
